import { LoadingButton } from '@mui/lab'
import { Alert, Button, Skeleton, Stack, Typography } from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import { OverlayState } from '@/hooks/useOverlay'
import useUsersById from '@/hooks/useUsersById'
import { useGetApprovalPolicy } from '@/service-library/hooks/approval-policies'
import { useGetApprovalPolicyVersion } from '@/service-library/hooks/approval-policy-versions'
import { useUpdatePolicyToLatest } from '@/service-library/hooks/document-approvals'
import { useGetPositions } from '@/service-library/hooks/positions'
import {
  ApprovalPolicyVersion,
  ApprovalStatePolicy,
  UserApproverState,
} from '@/types/approvals'
import { AuthUser } from '@/types/users'
import { isForbiddenError, isStaleStateError } from '@/utils/approval-actions'
import {
  ApproverRef,
  approverRefKey,
  orderedApproverRefs,
  orderedUserIds,
} from '@/utils/approver-entries'
import { orderRuleLabel } from '@/utils/policy-labels'
import {
  showErrorSnackbar,
  showInfoSnackbar,
  showSuccessSnackbar,
  showWarningSnackbar,
} from '@/utils/snackbars'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'

type UpdatePolicyVersionDialogProps = {
  overlay: OverlayState
  policy: ApprovalStatePolicy
  documentId: string
  /** The update fails if the document changed after this was read. */
  stateVersionId: string
  approvalStateQueryKey?: QueryKey
  /** Called when the update fails because the document changed underneath it. */
  onStaleState?: () => void
}

function approverLabel(
  ref: ApproverRef,
  usersById: Map<string, AuthUser>,
  positionNamesById: Map<string, string>,
): string {
  return ref.kind === 'position'
    ? positionNamesById.get(ref.positionId) || 'a position'
    : usersById.get(ref.userId)?.name || 'an approver'
}

type PolicyRules = Pick<
  ApprovalPolicyVersion,
  'approval_order' | 'approvals_required' | 'llm_trigger_prompt' | 'approvers'
>

type DiffSection = {
  label: string
  removed: string[]
  added: string[]
}

function describeRule(version: PolicyRules): string {
  return orderRuleLabel({
    approvalOrder: version.approval_order,
    approvalsRequired: version.approvals_required,
    approverCount: version.approvers.length,
  })
}

function buildVersionDiff(
  pinned: PolicyRules,
  latest: PolicyRules,
  usersById: Map<string, AuthUser>,
  positionNamesById: Map<string, string>,
): DiffSection[] {
  const sections: DiffSection[] = []

  const pinnedRefs = orderedApproverRefs(pinned.approvers)
  const latestRefs = orderedApproverRefs(latest.approvers)
  const pinnedKeys = new Set(pinnedRefs.map(approverRefKey))
  const latestKeys = new Set(latestRefs.map(approverRefKey))
  const label = (ref: ApproverRef) =>
    approverLabel(ref, usersById, positionNamesById)

  const removed = pinnedRefs
    .filter((ref) => !latestKeys.has(approverRefKey(ref)))
    .map(label)
  const added = latestRefs
    .filter((ref) => !pinnedKeys.has(approverRefKey(ref)))
    .map(label)
  if (removed.length > 0 || added.length > 0) {
    sections.push({ label: 'Approvers', removed, added })
  }

  if (describeRule(pinned) !== describeRule(latest)) {
    sections.push({
      label: 'Approvals needed',
      removed: [describeRule(pinned)],
      added: [describeRule(latest)],
    })
  }

  if (pinned.llm_trigger_prompt !== latest.llm_trigger_prompt) {
    sections.push({
      label: 'Applies when',
      removed: [pinned.llm_trigger_prompt],
      added: [latest.llm_trigger_prompt],
    })
  }

  return sections
}

/**
 * Approvals carry forward per person: one by someone still on the policy
 * keeps counting, one by someone removed stops. Only the people the new
 * version names outright count as still on it — an approval doesn't carry
 * through a position that happens to resolve to the same person, because
 * nothing here can tell that it does.
 */
function buildCarryLines(
  policy: ApprovalStatePolicy,
  latest: PolicyRules,
): string[] {
  const latestUserIds = new Set(orderedUserIds(latest.approvers))
  const lines: string[] = []

  const userApprovers = policy.approvers.filter(
    (approver): approver is UserApproverState => approver.kind === 'user',
  )
  const standing = userApprovers.filter(
    (approver) =>
      approver.latest_action?.type === 'approval' &&
      latestUserIds.has(approver.user_id),
  ).length
  const dropped = userApprovers.filter(
    (approver) =>
      approver.latest_action?.type === 'approval' &&
      !latestUserIds.has(approver.user_id),
  ).length
  if (standing > 0) {
    lines.push(
      `${standing} existing ${
        standing === 1 ? 'approval stands' : 'approvals stand'
      }.`,
    )
  }
  if (dropped > 0) {
    lines.push(
      `${dropped} existing ${
        dropped === 1 ? 'approval stops' : 'approvals stop'
      } counting (the approver was removed).`,
    )
  }

  return lines
}

export default function UpdatePolicyVersionDialog({
  overlay,
  policy,
  documentId,
  stateVersionId,
  approvalStateQueryKey,
  onStaleState,
}: UpdatePolicyVersionDialogProps) {
  const { approvalPolicyVersion: pinnedVersion, isPending: isPinnedPending } =
    useGetApprovalPolicyVersion({
      id: policy.pinned_approval_policy_version_id || '',
      enabled: overlay.isOpen,
    })

  // The policy detail renders its current version's rules flat, so "latest"
  // needs no version lookup of its own.
  const { approvalPolicy: latestVersion, isPending: isPolicyPending } =
    useGetApprovalPolicy({
      id: policy.approval_policy_id,
      enabled: overlay.isOpen,
    })

  const { usersById, isPending: isUsersPending } = useUsersById({
    enabled: overlay.isOpen,
  })

  // Only a version naming a position needs the catalog. The project id is
  // part of the condition because it comes from the policy: without it there
  // is nothing to query, and waiting on a query that can never run would hold
  // the dialog on a skeleton instead of letting it report the failure.
  const needsPositionNames =
    !!latestVersion?.project_id &&
    [pinnedVersion, latestVersion].some((version) =>
      version?.approvers.some(({ kind }) => kind === 'position'),
    )

  const { positions, isPending: isPositionsPending } = useGetPositions({
    filters: { limit: '1000', project_id: latestVersion?.project_id || '' },
    enabled: overlay.isOpen && needsPositionNames,
  })

  const positionNamesById = new Map(positions.map(({ id, name }) => [id, name]))

  // Names gate the render too, so one never shows as "an approver" and then
  // swaps to the real one.
  const isLoading =
    isPinnedPending ||
    isPolicyPending ||
    isUsersPending ||
    (needsPositionNames && isPositionsPending)

  const diffSections =
    pinnedVersion && latestVersion
      ? buildVersionDiff(
          pinnedVersion,
          latestVersion,
          usersById,
          positionNamesById,
        )
      : []
  const carryLines =
    pinnedVersion && latestVersion ? buildCarryLines(policy, latestVersion) : []

  const { updatePolicyToLatest, isPending: isUpdating } =
    useUpdatePolicyToLatest({
      documentId,
      approvalPolicyId: policy.approval_policy_id,
      approvalStateQueryKey,
      onSuccess: (data) => {
        overlay.close()
        if (data.policy_match) {
          showInfoSnackbar(
            'The policy conditions changed, so it is being re-checked against this document. ' +
              'It may no longer apply.',
          )
          return
        }
        if (data.already_current) {
          showInfoSnackbar('This document is already on the latest version.')
          return
        }
        showSuccessSnackbar('Document updated to the latest policy version.')
      },
      onError: (error) => {
        if (isStaleStateError(error)) {
          showWarningSnackbar(
            'The document changed while you were reviewing this update. Please try again.',
          )
          onStaleState?.()
          return
        }
        if (isForbiddenError(error)) {
          showErrorSnackbar(
            'Updating a document to the latest policy version needs the policy-edit permission.',
          )
          return
        }
        showErrorSnackbar(
          'Failed to update the policy version. Please try again later.',
        )
        overlay.close()
      },
    })

  const versionsLoaded = !isLoading && !!pinnedVersion && !!latestVersion

  return (
    <Dialog
      title="Update to latest version"
      subtitle={policy.description}
      maxWidth="sm"
      {...overlay}
    >
      <DialogContent>
        <Stack spacing={2}>
          {isLoading && (
            <Stack spacing={1}>
              <Skeleton width="40%" />
              <Skeleton />
              <Skeleton />
            </Stack>
          )}

          {versionsLoaded && (
            <>
              <Typography variant="body2">
                This policy is on a newer version than this document.
                Here&apos;s what changed:
              </Typography>

              {diffSections.length > 0 ? (
                <Stack
                  spacing={1.5}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.5,
                  }}
                >
                  {diffSections.map((section) => (
                    <Stack key={section.label} spacing={0.25}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600 }}
                      >
                        {section.label}
                      </Typography>
                      {section.removed.map((value) => (
                        <Typography
                          key={value}
                          variant="body2"
                          color="error.main"
                        >
                          {`− ${value}`}
                        </Typography>
                      ))}
                      {section.added.map((value) => (
                        <Typography
                          key={value}
                          variant="body2"
                          color="success.main"
                        >
                          {`+ ${value}`}
                        </Typography>
                      ))}
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  The approvers, approval rule, and conditions are unchanged.
                </Typography>
              )}

              {carryLines.map((line) => (
                <Typography key={line} variant="body2">
                  {line}
                </Typography>
              ))}
            </>
          )}

          {!isLoading && !versionsLoaded && (
            <Alert severity="error">
              Couldn&apos;t load this policy&apos;s versions. Please try again
              later.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogFooter>
        <Button variant="text" onClick={overlay.close}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={isUpdating}
          disabled={!versionsLoaded}
          onClick={() =>
            updatePolicyToLatest({ expected_state_version_id: stateVersionId })
          }
        >
          Update Document
        </LoadingButton>
      </DialogFooter>
    </Dialog>
  )
}
